import React, { useState, useEffect } from 'react';
import { 
  Plus, 
  Edit3, 
  Trash2, 
  Search,
  Filter,
  Image as ImageIcon,
  Upload,
  X,
  Save
} from 'lucide-react';
import { blogsAPI, getImageUrl, blogCategories } from '../services/api';
import { useToast } from '../contexts/ToastContext';
import {
  BlogsContainer,
  SectionHeader,
  Title,
  Subtitle,
  ActionBar,
  FilterSection,
  Select,
  Input,
  ActionButton,
  DangerButton,
  EditButton,
  BlogsGrid,
  BlogCard,
  BlogImage,
  BlogContent,
  BlogTitle,
  BlogExcerpt,
  BlogMeta,
  BlogCategory,
  BlogDate,
  BlogTags,
  BlogTag,
  BlogActions,
  Pagination,
  PageButton,
  LoadingSpinner,
  ErrorMessage,
  EmptyState,
  EmptyStateIcon,
  EmptyStateText,
  Modal,
  ModalContent,
  ModalHeader,
  ModalTitle,
  CloseButton,
  Form,
  FormGroup,
  Label,
  TextArea,
  ImageUpload,
  ImageInput,
  ImageLabel,
  ImagePreview,
  ImagePlaceholder,
  TagsInput,
  Tag,
  RemoveTagButton,
  TagInput,
  SectionsContainer,
  SectionCard,
  SectionCardHeader,
  SectionTitle,
  RemoveSectionButton,
  SubmitButton
} from './BlogsSection.styles';

const BlogsSection = () => {
  const { showSuccess, showError } = useToast();
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({});
  const [showModal, setShowModal] = useState(false);
  const [editingBlog, setEditingBlog] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    paragraph_explanation: '',
    category: '',
    author_name: '',
    tags: [],
    sections: [],
    image: null
  });
  const [newTag, setNewTag] = useState('');
  const [filters, setFilters] = useState({
    page: 1,
    per_page: 10,
    category: '',
    author: '',
    tag: '',
    sort_by: 'created_at',
    sort_order: 'desc'
  });

  // Helper function to get category name by ID
  const getCategoryName = (categoryId) => {
    const category = blogCategories.find(cat => cat.id === categoryId);
    return category ? category.name : categoryId;
  };

  // Helper function to validate category ID
  const isValidCategory = (categoryId) => {
    return blogCategories.some(cat => cat.id === categoryId && cat.id !== 'all');
  };

  useEffect(() => {
    loadBlogs();
  }, [filters]);

  const loadBlogs = async () => {
    try {
      setLoading(true);
      const params = { ...filters };
      // Remove empty filter values
      Object.keys(params).forEach(key => {
        if (params[key] === '') {
          delete params[key];
        }
      });
      
      const response = await blogsAPI.get(params);
      if (response.status === 200) {
        setBlogs(response.data.blogs || []);
        setPagination(response.data.pagination || {});
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to load blogs';
      showError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value,
      page: 1 // Reset to first page when filters change
    }));
  };

  const handlePageChange = (page) => {
    setFilters(prev => ({ ...prev, page }));
  };

  const openCreateModal = () => {
    setEditingBlog(null);
    setFormData({
      title: '',
      paragraph_explanation: '',
      category: '',
      author_name: '',
      tags: [],
      sections: [],
      image: null
    });
    setShowModal(true);
  };

  const openEditModal = (blog) => {
    setEditingBlog(blog);
    // Ensure sections have the correct structure (without content_type)
    const sections = (blog.sections || []).map(section => ({
      header: section.header || '',
      explanation: section.explanation || ''
    }));
    
    setFormData({
      title: blog.title,
      paragraph_explanation: blog.paragraph_explanation,
      category: blog.category,
      author_name: blog.author_name,
      tags: blog.tags || [],
      sections: sections,
      image: null
    });
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingBlog(null);
    setFormData({
      title: '',
      paragraph_explanation: '',
      category: '',
      author_name: '',
      tags: [],
      sections: [],
      image: null
    });
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleImageChange = (file) => {
    setFormData(prev => ({
      ...prev,
      image: file
    }));
  };

  const addTag = () => {
    if (newTag.trim() && !formData.tags.includes(newTag.trim())) {
      setFormData(prev => ({
        ...prev,
        tags: [...prev.tags, newTag.trim()]
      }));
      setNewTag('');
    }
  };

  const removeTag = (tagToRemove) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToRemove)
    }));
  };

  const addSection = () => {
    setFormData(prev => ({
      ...prev,
      sections: [...prev.sections, {
        header: '',
        explanation: ''
      }]
    }));
  };

  const removeSection = (index) => {
    setFormData(prev => ({
      ...prev,
      sections: prev.sections.filter((_, i) => i !== index)
    }));
  };

  const handleSectionChange = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      sections: prev.sections.map((section, i) => 
        i === index ? { ...section, [field]: value } : section
      )
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title.trim() || !formData.paragraph_explanation.trim() || 
        !formData.category.trim() || !formData.author_name.trim()) {
      setError('Please fill in all required fields');
      return;
    }

    if (!isValidCategory(formData.category)) {
      setError('Please select a valid category');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const submitData = new FormData();
      submitData.append('title', formData.title);
      
      // Preserve line breaks in paragraph explanation
      submitData.append('paragraph_explanation', formData.paragraph_explanation.replace(/\n/g, '\n'));
      
      submitData.append('category', formData.category);
      submitData.append('author_name', formData.author_name);
      submitData.append('tags', JSON.stringify(formData.tags));
      
      // Process sections to preserve line breaks in explanations
      const processedSections = formData.sections.map(section => ({
        header: section.header,
        explanation: section.explanation.replace(/\n/g, '\n') // Preserve line breaks
      }));
      submitData.append('sections_data', JSON.stringify(processedSections));

      if (formData.image) {
        submitData.append('image', formData.image);
      }

      let response;
      if (editingBlog) {
        response = await blogsAPI.update(editingBlog.id, submitData);
      } else {
        response = await blogsAPI.create(submitData);
      }

      if (response.status === 200 || response.status === 201) {
        showSuccess(editingBlog ? 'Blog updated successfully!' : 'Blog created successfully!');
        closeModal();
        loadBlogs();
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to save blog';
      showError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (blogId) => {
    if (window.confirm('Are you sure you want to delete this blog?')) {
      try {
        setLoading(true);
        await blogsAPI.delete(blogId);
        showSuccess('Blog deleted successfully!');
        loadBlogs();
      } catch (error) {
        const errorMessage = error.response?.data?.message || 'Failed to delete blog';
        showError(errorMessage);
      } finally {
        setLoading(false);
      }
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <BlogsContainer>
      <SectionHeader>
        <Title>Blogs Management</Title>
        <Subtitle>Manage your blog posts with flexible content sections and filtering</Subtitle>
      </SectionHeader>

      {error && <ErrorMessage>{error}</ErrorMessage>}

      <ActionBar>
        <FilterSection>
          <Select
            value={filters.category}
            onChange={(e) => handleFilterChange('category', e.target.value)}
          >
            <option value="">All Categories</option>
            {blogCategories.filter(cat => cat.id !== 'all').map(category => (
              <option key={category.id} value={category.id}>
                {category.name}
              </option>
            ))}
          </Select>
          
          <Input
            type="text"
            placeholder="Filter by author..."
            value={filters.author}
            onChange={(e) => handleFilterChange('author', e.target.value)}
          />
          
          <Input
            type="text"
            placeholder="Filter by tag..."
            value={filters.tag}
            onChange={(e) => handleFilterChange('tag', e.target.value)}
          />
          
          <Select
            value={filters.sort_by}
            onChange={(e) => handleFilterChange('sort_by', e.target.value)}
          >
            <option value="created_at">Sort by Date</option>
            <option value="title">Sort by Title</option>
            <option value="author">Sort by Author</option>
            <option value="category">Sort by Category</option>
          </Select>
          
          <Select
            value={filters.sort_order}
            onChange={(e) => handleFilterChange('sort_order', e.target.value)}
          >
            <option value="desc">Newest First</option>
            <option value="asc">Oldest First</option>
          </Select>
        </FilterSection>
        
        <ActionButton onClick={openCreateModal}>
          <Plus size={16} />
          Create Blog
        </ActionButton>
      </ActionBar>

      {loading && blogs.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '4rem' }}>
          <LoadingSpinner />
          <p style={{ color: '#64748B', marginTop: '1rem' }}>Loading blogs...</p>
        </div>
      ) : blogs.length === 0 ? (
        <EmptyState>
          <EmptyStateIcon>📝</EmptyStateIcon>
          <EmptyStateText>No blogs found. Create your first blog post!</EmptyStateText>
        </EmptyState>
      ) : (
        <>
          <BlogsGrid>
            {blogs.map((blog) => (
              <BlogCard key={blog.id}>
                <BlogImage>
                  {blog.image_path ? (
                    <img
                      src={getImageUrl(blog.image_path)}
                      alt={blog.title}
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                  ) : (
                    <ImageIcon size={48} />
                  )}
                </BlogImage>
                <BlogContent>
                  <BlogTitle>{blog.title}</BlogTitle>
                  <BlogExcerpt>{blog.paragraph_explanation}</BlogExcerpt>
                  <BlogMeta>
                    <BlogCategory>
                      {getCategoryName(blog.category)}
                    </BlogCategory>
                    <BlogDate>{formatDate(blog.created_at)}</BlogDate>
                  </BlogMeta>
                  {blog.tags && blog.tags.length > 0 && (
                    <BlogTags>
                      {blog.tags.slice(0, 3).map((tag, index) => (
                        <BlogTag key={index}>{tag}</BlogTag>
                      ))}
                      {blog.tags.length > 3 && (
                        <BlogTag>+{blog.tags.length - 3} more</BlogTag>
                      )}
                    </BlogTags>
                  )}
                  <BlogActions>
                    <EditButton onClick={() => openEditModal(blog)}>
                      <Edit3 size={14} />
                      Edit
                    </EditButton>
                    <DangerButton onClick={() => handleDelete(blog.id)}>
                      <Trash2 size={14} />
                      Delete
                    </DangerButton>
                  </BlogActions>
                </BlogContent>
              </BlogCard>
            ))}
          </BlogsGrid>

          {pagination.pages > 1 && (
            <Pagination>
              <PageButton
                disabled={filters.page === 1}
                onClick={() => handlePageChange(filters.page - 1)}
              >
                Previous
              </PageButton>
              
              {Array.from({ length: Math.min(5, pagination.pages) }, (_, i) => {
                const page = i + 1;
                return (
                  <PageButton
                    key={page}
                    active={filters.page === page}
                    onClick={() => handlePageChange(page)}
                  >
                    {page}
                  </PageButton>
                );
              })}
              
              <PageButton
                disabled={filters.page === pagination.pages}
                onClick={() => handlePageChange(filters.page + 1)}
              >
                Next
              </PageButton>
            </Pagination>
          )}

          {/* Add Blog Button at Bottom */}
          <div style={{ textAlign: 'center', marginTop: '2rem' }}>
            <ActionButton onClick={openCreateModal}>
              <Plus size={16} />
              Create New Blog
            </ActionButton>
          </div>
        </>
      )}

      {showModal && (
        <Modal>
          <ModalContent>
            <ModalHeader>
              <ModalTitle>
                {editingBlog ? 'Edit Blog' : 'Create New Blog'}
              </ModalTitle>
              <CloseButton onClick={closeModal}>
                <X size={20} />
              </CloseButton>
            </ModalHeader>

            <Form onSubmit={handleSubmit}>
              <FormGroup>
                <Label htmlFor="title">Title *</Label>
                <Input
                  id="title"
                  type="text"
                  value={formData.title}
                  onChange={(e) => handleInputChange('title', e.target.value)}
                  placeholder="Blog title..."
                  required
                />
              </FormGroup>

              <FormGroup>
                <Label htmlFor="paragraph_explanation">Description *</Label>
                <TextArea
                  id="paragraph_explanation"
                  value={formData.paragraph_explanation}
                  onChange={(e) => handleInputChange('paragraph_explanation', e.target.value)}
                  placeholder="Blog description..."
                  required
                />
              </FormGroup>

              <FormGroup>
                <Label htmlFor="category">Category *</Label>
                <Select
                  id="category"
                  value={formData.category}
                  onChange={(e) => handleInputChange('category', e.target.value)}
                  required
                >
                  <option value="">Select a category...</option>
                  {blogCategories.filter(cat => cat.id !== 'all').map(category => (
                    <option key={category.id} value={category.id}>
                      {category.name}
                    </option>
                  ))}
                </Select>
              </FormGroup>

              <FormGroup>
                <Label htmlFor="author_name">Author *</Label>
                <Input
                  id="author_name"
                  type="text"
                  value={formData.author_name}
                  onChange={(e) => handleInputChange('author_name', e.target.value)}
                  placeholder="Author name..."
                  required
                />
              </FormGroup>

              <FormGroup>
                <Label>Tags</Label>
                <TagsInput>
                  {formData.tags.map((tag, index) => (
                    <Tag key={index}>
                      {tag}
                      <RemoveTagButton onClick={() => removeTag(tag)}>
                        <X size={12} />
                      </RemoveTagButton>
                    </Tag>
                  ))}
                  <TagInput
                    type="text"
                    value={newTag}
                    onChange={(e) => setNewTag(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())}
                    placeholder="Add tag..."
                  />
                </TagsInput>
              </FormGroup>

              <FormGroup>
                <Label>Featured Image</Label>
                <ImageUpload>
                  <ImageInput
                    type="file"
                    id="image"
                    accept="image/*"
                    onChange={(e) => handleImageChange(e.target.files[0])}
                  />
                  <ImageLabel htmlFor="image">
                    <Upload size={16} />
                    Choose Image
                  </ImageLabel>
                  <ImagePreview>
                    {formData.image ? (
                      <img
                        src={URL.createObjectURL(formData.image)}
                        alt="Preview"
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      />
                    ) : editingBlog && editingBlog.image_path ? (
                      <img
                        src={getImageUrl(editingBlog.image_path)}
                        alt="Current"
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      />
                    ) : (
                      <ImagePlaceholder>
                        <ImageIcon size={48} />
                        <span>No image selected</span>
                      </ImagePlaceholder>
                    )}
                  </ImagePreview>
                </ImageUpload>
              </FormGroup>

              <FormGroup>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                  <Label>Sections</Label>
                  <ActionButton type="button" onClick={addSection}>
                    <Plus size={16} />
                    Add Section
                  </ActionButton>
                </div>
                
                <SectionsContainer>
                  {formData.sections.map((section, index) => (
                    <SectionCard key={index}>
                      <SectionCardHeader>
                        <SectionTitle>Section {index + 1}</SectionTitle>
                        <RemoveSectionButton type="button" onClick={() => removeSection(index)}>
                          <X size={12} />
                        </RemoveSectionButton>
                      </SectionCardHeader>
                      
                      <FormGroup>
                        <Label>Header</Label>
                        <Input
                          type="text"
                          value={section.header}
                          onChange={(e) => handleSectionChange(index, 'header', e.target.value)}
                          placeholder="Section header..."
                        />
                      </FormGroup>
                      
                      <FormGroup>
                        <Label>Explanation</Label>
                        <TextArea
                          value={section.explanation}
                          onChange={(e) => handleSectionChange(index, 'explanation', e.target.value)}
                          placeholder="Section content with manual formatting (e.g., 1. First point, 2. Second point, or • Bullet point)"
                          style={{ minHeight: '120px' }}
                        />
                        <div style={{ fontSize: '0.875rem', color: '#64748B', marginTop: '0.5rem' }}>
                          <strong>Formatting tips:</strong>
                          <br />• Use numbers (1, 2, 3...) for step-by-step processes
                          <br />• Use bullets (•) for feature lists
                          <br />• Use roman numerals (i, ii, iii...) for formal lists
                          <br />• Use any other formatting as needed
                        </div>
                      </FormGroup>
                    </SectionCard>
                  ))}
                </SectionsContainer>
              </FormGroup>

              <SubmitButton type="submit" disabled={loading}>
                {loading ? (
                  <LoadingSpinner />
                ) : (
                  <>
                    <Save size={20} />
                    {editingBlog ? 'Update Blog' : 'Create Blog'}
                  </>
                )}
              </SubmitButton>
            </Form>
          </ModalContent>
        </Modal>
      )}
    </BlogsContainer>
  );
};

export default BlogsSection;
